import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Shield,
  Zap,
  Wallet,
  Receipt,
  Download,
  Undo2,
  Sparkles,
  Server,
  Users2,
  Palette,
  Boxes,
  Code2,
  Plus,
  Minus,
  MessageCircle,
  LifeBuoy,
  Mail,
  Star,
  Instagram
} from "lucide-react";

import { useEffect, useState } from "react";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { formatDate } from "@/lib/format";

// LostDRKC theme — dark glassmorphism + yellow accents.
const THEME = {
  bg: "#0b0b0b",
  primary: "#fbce18",
  primarySoft: "rgba(251,206,24,0.14)",
  card: "rgba(255,255,255,0.05)",
  border: "rgba(251,206,24,0.22)",
};

export default function Landing() {
  const [openFaq, setOpenFaq] = useState(0);
  const [feedback, setFeedback] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(
            "/public/feedback?website_id=payments"
        );
        if (alive) setFeedback(data || []);
      } catch (e) { /* ignore */ }
    })();
    return () => { alive = false; };
  }, []);

  return (
<div
  className="relative min-h-screen landing-bg"
  style={{
    color: "#f4f4f8",
  }}
>
<div className="landing-effects">
  <div className="landing-light light1"></div>
  <div className="landing-light light2"></div>
  <div className="landing-light light3"></div>

  <div className="landing-particle p1"></div>
  <div className="landing-particle p2"></div>
  <div className="landing-particle p3"></div>
  <div className="landing-particle p4"></div>
  <div className="landing-particle p5"></div>

  <div className="landing-glass"></div>
</div>


      <div className="absolute inset-0 pointer-events-none opacity-[0.04]" style={{ backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>")` }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-6">
        {/* Nav */}
        <header className="flex items-center justify-between">
          <Logo size={54} />
          <div className="hidden md:flex items-center gap-6 text-sm text-white/70">
            <a href="#services" className="hover:text-white transition-colors">Services</a>
            <a href="#features" className="hover:text-white transition-colors">Why us</a>
            <a href="#feedback" className="hover:text-white transition-colors">Feedback</a>
            <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
            <a href="#contact" className="hover:text-white transition-colors">Support</a>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="landing-login-btn" className="text-sm text-white/80 hover:text-white transition-colors px-3 py-2">Sign in</Link>
            <Link to="/login" data-testid="landing-cta-btn" className="text-sm px-4 py-2 rounded-lg font-semibold transition-transform hover:scale-[1.02]" style={{ background: THEME.primary, color: "#0b0b0b" }}>
              Open App
            </Link>
          </div>
        </header>

        {/* Hero (no live payments ticker) */}
        <section className="mt-20 md:mt-28 max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] tracking-[0.22em] uppercase" style={{ background: THEME.primarySoft, border: `1px solid ${THEME.border}`, color: THEME.primary }} data-testid="landing-hero-badge">
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: THEME.primary }} />
            LostDRKC Ecosystem · Unified Payments
          </div>
          <h1 className="mt-6 font-display font-bold tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-6xl">
            LostDRKC Payments
            <br />
            <span style={{ background: `linear-gradient(90deg, ${THEME.primary} 20%, #ffe786 60%, ${THEME.primary} 100%)`, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
              One payment solution for every LostDC service.
            </span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed" style={{ color: "rgba(255,255,255,0.65)" }}>
            Pay securely across LostDC Hosting, LostDRKC, LostDC Network, Casper Wearables and LostDC Projects — through one unified checkout.
          </p>
          <div className="mt-5 flex flex-wrap gap-2 text-[11px] tracking-[0.22em] uppercase" style={{ color: "rgba(255,255,255,0.5)" }}>
            <span>Fast</span><span>·</span><span>Secure</span><span>·</span><span>Trusted</span><span>·</span><span style={{ color: THEME.primary }}>Unified</span>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/login" data-testid="landing-primary-cta" className="px-6 py-3 rounded-lg text-sm font-semibold inline-flex items-center gap-2 transition-transform hover:scale-[1.02]" style={{ background: THEME.primary, color: "#0b0b0b" }}>
              Get started <ArrowUpRight size={16}/>
            </Link>
            <a href="#services" className="px-6 py-3 rounded-lg text-sm inline-flex items-center gap-2" style={{ border: `1px solid ${THEME.border}`, color: "rgba(255,255,255,0.85)" }}>
              Explore services
            </a>
          </div>
        </section>

        {/* Services */}
        <section id="services" className="mt-24 md:mt-32">
          <div className="text-[10px] tracking-[0.28em] uppercase mb-3" style={{ color: THEME.primary }}>Services</div>
          <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight max-w-2xl">Every LostDC service. One checkout.</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s) => (
              <div
                key={s.title}
                className="p-6 rounded-2xl transition-transform hover:-translate-y-1"
                style={{ background: THEME.card, border: `1px solid ${THEME.border}`, backdropFilter: "blur(24px)" }}
                data-testid={`service-${s.slug}`}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, rgba(251,206,24,0.25), rgba(251,206,24,0.10))`, color: THEME.primary, border: `1px solid ${THEME.border}` }}
                >
                  <s.icon size={18}/>
                </div>
                <h3 className="mt-5 font-display text-xl tracking-tight">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.6)" }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section id="features" className="mt-24 md:mt-32">
          <div className="text-[10px] tracking-[0.28em] uppercase mb-3" style={{ color: THEME.primary }}>Why LostDRKC Payments</div>
          <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight max-w-2xl">One wallet, every LostDC service.</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="p-5 rounded-2xl flex gap-3" style={{ background: THEME.card, border: `1px solid ${THEME.border}` }}>
                <div className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: THEME.primarySoft, color: THEME.primary, border: `1px solid ${THEME.border}` }}>
                  <f.icon size={16}/>
                </div>
                <div>
                  <div className="font-semibold">{f.title}</div>
                  <div className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.55)" }}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Feedback from real users */}
        <section id="feedback" className="mt-24 md:mt-32">
          <div className="text-[10px] tracking-[0.28em] uppercase mb-3" style={{ color: THEME.primary }}>Loved by the community</div>
          <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight max-w-2xl">Real feedback from real customers.</h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="landing-feedback">
            {feedback.length === 0 && (
              <div className="col-span-full text-sm text-white/45 text-center py-8">
                Be the first to leave a review — sign in and drop us a note from your dashboard.
              </div>
            )}
            {feedback.map((t) => (
              <div key={t.id} className="p-6 rounded-2xl relative" style={{ background: THEME.card, border: `1px solid ${t.pinned ? "rgba(251,206,24,0.5)" : THEME.border}` }} data-testid={`landing-testimonial-${t.id}`}>
                {t.pinned && (
                  <div className="absolute top-3 right-3 text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full" style={{ background: THEME.primary, color: "#0b0b0b", fontWeight: 700 }}>Pinned</div>
                )}
                {typeof t.rating === "number" && (
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={14} fill={i < t.rating ? THEME.primary : "transparent"} color={i < t.rating ? THEME.primary : "rgba(255,255,255,0.25)"} />
                    ))}
                  </div>
                )}
                <div className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>"{t.comment}"</div>
                <div className="mt-5 flex items-center gap-3">
                  {t.avatar ? (
                    <img src={t.avatar} alt="" className="w-9 h-9 rounded-full object-cover border" style={{ borderColor: THEME.border }} />
                  ) : (
                    <div className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-xs" style={{ background: THEME.primarySoft, color: THEME.primary }}>{t.name?.[0]?.toUpperCase()}</div>
                  )}
                  <div>
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="text-[11px]" style={{ color: "rgba(255,255,255,0.45)" }}>
                      {t.service ? `${t.service} · ` : ""}{formatDate(t.created_at)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="mt-24 md:mt-32">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="text-[10px] tracking-[0.28em] uppercase mb-3" style={{ color: THEME.primary }}>Answers</div>
              <h2 className="font-display font-semibold text-3xl tracking-tight">Frequently asked.</h2>
              <p className="mt-3 text-sm" style={{ color: "rgba(255,255,255,0.55)" }}>Still curious? Ping us on Discord.</p>
            </div>
            <div className="md:col-span-8 space-y-2">
              {FAQS.map((f, i) => (
                <button
                  key={i}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full text-left p-5 rounded-2xl transition-colors"
                  style={{ background: THEME.card, border: `1px solid ${THEME.border}` }}
                  data-testid={`faq-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{f.q}</div>
                    {openFaq === i ? <Minus size={16} color={THEME.primary} /> : <Plus size={16} color={THEME.primary} />}
                  </div>
                  {openFaq === i && (
                    <div className="mt-3 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.7)" }}>{f.a}</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="mt-24 md:mt-32">
          <div className="rounded-3xl p-8 md:p-12 relative overflow-hidden" style={{ background: `linear-gradient(135deg, rgba(251,206,24,0.18), rgba(251,206,24,0.06)), ${THEME.card}`, border: `1px solid ${THEME.border}` }}>
            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: THEME.primary }}>Support & community</div>
                <h2 className="mt-3 font-display font-semibold text-3xl md:text-4xl tracking-tight">Need help? We're a message away.</h2>
                <p className="mt-3 text-sm max-w-lg" style={{ color: "rgba(255,255,255,0.65)" }}>Reach out through Discord or email — most tickets are resolved within a few hours.</p>
              </div>
              <div className="md:col-span-4 flex flex-col gap-3">
                <a href="https://lostdrkc.in/discord" target="_blank" rel="noreferrer" className="rounded-xl px-5 py-4 inline-flex items-center gap-3 font-semibold" style={{ background: "#5865F2", color: "#fff" }} data-testid="landing-discord">
                  <MessageCircle size={16}/> Join our Discord
                </a>
                <a href="mailto:support@lostdrkc.in" className="rounded-xl px-5 py-4 inline-flex items-center gap-3 font-semibold" style={{ background: THEME.primary, color: "#0b0b0b" }} data-testid="landing-email">
                  <Mail size={16}/> support@lostdrkc.in
                </a>
             <a
  href="https://lostdrkc.in/instagram"
  target="_blank"
  rel="noreferrer"
  className="rounded-xl px-5 py-4 inline-flex items-center gap-3 font-semibold transition-all duration-300 hover:scale-[1.02] hover:shadow-lg"
  style={{
    background:
      "linear-gradient(135deg, #F58529 0%, #FEDA77 20%, #DD2A7B 55%, #8134AF 80%, #515BD4 100%)",
    color: "#fff",
    boxShadow: "0 8px 30px rgba(221,42,123,0.25)"
  }}
  data-testid="landing-instagram"
>
  <Instagram size={16} />
  Follow us on Instagram
</a>
              </div>
            </div>
          </div>
        </section>

        <footer className="mt-20 pb-10 flex flex-wrap items-center justify-between gap-3">
          <Logo size={52} />
          <div className="text-xs tracking-[0.28em] uppercase" style={{ color: "rgba(255,255,255,0.4)" }} >© 2024-{new Date().getFullYear()} LOSTDRKC. All rights reserved.</div>
        </footer>
      </div>
    </div>
  );
}

const SERVICES = [
  { slug: "hosting", title: "LostDC Hosting", desc: "Minecraft Hosting, VPS, Web Hosting — powered by high-performance infra.", icon: Server },
  { slug: "network", title: "LostDC Network", desc: "Ranks, Coins, Season Passes and in-game currency top-ups.", icon: Zap },
  { slug: "lostdrkc", title: "LostDRKC", desc: "Community services, event tickets and creator drops.", icon: Users2 },
  { slug: "casper", title: "Casper Wearables", desc: "Official merchandise, apparel and lifestyle drops.", icon: Palette },
  { slug: "projects", title: "LostDC Projects", desc: "Custom software builds and development retainers.", icon: Code2 },
  { slug: "future", title: "Future services", desc: "New LostDC services — you'll never need another payment page.", icon: Boxes },
];

const FEATURES = [
  { title: "One Wallet, All Services", desc: "Pay once, remembered across every LostDRKC brand.", icon: Wallet },
  { title: "Instant Payment Verification", desc: "Razorpay-native capture — links flip to paid in seconds.", icon: Zap },
  { title: "Secure Razorpay Integration", desc: "PCI-DSS, tokenized cards, 2FA-backed OTP flows.", icon: Shield },
  { title: "Payment History & Statements", desc: "Complete ledger with running balance, downloadable.", icon: Receipt },
  { title: "Download Invoices Anytime", desc: "PDF invoice with QR & refund reflection built-in.", icon: Download },
  { title: "Fast Refund Processing", desc: "48-hour window refunds, admin-approved, notify-on-status.", icon: Undo2 },
  { title: "Unified LostDRKC Ecosystem", desc: "Single sign-on across upcoming LostDC surfaces.", icon: Sparkles },
];

const FAQS = [
  { q: "Which payment methods are supported?", a: "We support UPI, Credit Cards, Debit Cards, Net Banking, and selected Wallets through our trusted payment partners." },
  { q: "How secure is my payment?", a: "All card, UPI and netbanking transactions are processed on Razorpay's PCI-DSS Level 1 certified infrastructure. We never store card details." },
  { q: "Can I get a refund?", a: "Yes — refunds are available within 48 hours of a successful capture, subject to admin approval. Submit a request to your branch admin or reach out via support." },
  { q: "Do you support GST invoices?", a: "Yes. Every invoice supports GSTIN, downloadable as PDF, and includes fee + tax breakdowns from the payment gateway." },
  {q: "How long does it take to receive my payment confirmation?", a: "Most payments are confirmed instantly after successful processing. In rare cases, bank or payment gateway delays may occur, and confirmation can take up to 30 minutes." },
];
